import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { ApiReservationInvitee, PaginatedResponse } from "@/types/api";

const INVITEE_FIELDS = [
  "id",
  "reservation_id",
  "guest_id",
  "name",
  "phone",
  "email",
  "created_at",
] as const satisfies readonly (keyof ApiReservationInvitee)[];

function inviteeKey(reservationId: string) {
  return ["reservations", reservationId, "invitees"] as const;
}

export function useInvitees(reservationId: string | null) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: reservationId ? inviteeKey(reservationId) : ["reservations", "invitees", "disabled"],
    queryFn: async () => {
      if (!reservationId) return [] as ApiReservationInvitee[];
      const params = new URLSearchParams({ fields: INVITEE_FIELDS.join(",") });
      const res = await api
        .get(`reservations/${reservationId}/invitees/?${params}`)
        .json<PaginatedResponse<ApiReservationInvitee>>();
      return res.items;
    },
    enabled: isAuthenticated && !!reservationId,
  });
}

export function useAddInvitee(reservationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { name: string; phone?: string; email?: string }) =>
      api
        .post(`reservations/${reservationId}/invitees/`, { json: data })
        .json<ApiReservationInvitee>(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteeKey(reservationId) });
    },
  });
}

export function useDeleteInvitee(reservationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteeId: string) =>
      api.delete(`reservations/${reservationId}/invitees/${inviteeId}/`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inviteeKey(reservationId) });
    },
  });
}
